import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useProjects } from "../../context/ProjectsContext";
import { useAuth } from "../../context/AuthContext";
import {
  billingAccessActionLabel,
  getProjectBillingAccess,
} from "../../utils/projectBillingAccess";
import { isSuperAdmin } from "../../utils/roles";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

export default function ProjectBillingGate({ children, featureName = "esta función" }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject } = useProjects();
  const superAdmin = isSuperAdmin(user);

  const project = React.useMemo(() => {
    const matched = projects.find(
      (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
    );
    const activeMatches =
      activeProject?.slug === projectId ||
      activeProject?.id === projectId ||
      activeProject?._id === projectId;
    return matched || (activeMatches ? activeProject : null);
  }, [projects, activeProject, projectId]);

  const access = getProjectBillingAccess(project);
  // El superadmin necesita acceso técnico aun cuando el proyecto esté vencido,
  // suspendido por billing o en modo solo landing.
  if (superAdmin || !project || access.isOperational) return children;

  const billingPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;
  const settingsPath = `/dashboard/proyectos/${projectId}/configuracion/general`;
  const landingPath = `/dashboard/proyectos/${projectId}/pagina`;
  const paymentSuspended = access.reason === "billing_suspended";
  const billingRestricted = access.isLandingOnly || paymentSuspended;
  const trialEnded = access.reason === "trial_expired";
  const actionPath = billingRestricted ? billingPath : settingsPath;
  const actionLabel = billingAccessActionLabel(access);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 5,
        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
        overflow: "hidden",
        boxShadow: "0 22px 55px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.08)",
      }}
    >
      <Box
        sx={{
          p: { xs: 3, md: 5 },
          color: "#fff",
          background: "linear-gradient(135deg,#19242F 0%,#30485B 100%)",
        }}
      >
        <Stack spacing={2.5} alignItems="flex-start">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 3.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(243,90,58,.18)",
              color: "#FF8B72",
            }}
          >
            <LockClockRoundedIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 950, letterSpacing: ".12em", color: "rgba(255,255,255,.62)" }}>
              {trialEnded
                ? "PRUEBA FINALIZADA"
                : paymentSuspended
                  ? "SUSPENDIDO POR PAGO"
                  : billingRestricted
                    ? "ACCESO LIMITADO"
                    : "PROYECTO SUSPENDIDO"}
            </Typography>
            <Typography variant="h3" sx={{ mt: 0.3, fontWeight: 950, letterSpacing: "-.04em" }}>
              {featureName} está temporalmente pausada
            </Typography>
            <Typography sx={{ mt: 1.2, maxWidth: 760, color: "rgba(255,255,255,.76)" }}>
              {trialEnded
                ? "No borramos nada. La landing continúa disponible y la información del proyecto está segura. Elegí la suscripción que querés conservar para volver a habilitar las herramientas operativas."
                : paymentSuspended
                  ? "El proyecto está suspendido por pago y sus datos siguen seguros. Regularizá la suscripción para recuperar el acceso operativo."
                  : billingRestricted
                    ? "No borramos nada. La información del proyecto está segura y la landing continúa disponible. Al regularizar la suscripción, WebProfit vuelve a habilitar las herramientas operativas automáticamente."
                    : "El proyecto fue suspendido administrativamente. Los datos permanecen guardados hasta que un administrador vuelva a habilitarlo."}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Button
              variant="contained"
              startIcon={<CreditCardRoundedIcon />}
              onClick={() => navigate(actionPath)}
              sx={{ bgcolor: BRAND.orange, borderRadius: 999, px: 3, textTransform: "none", fontWeight: 950 }}
            >
              {actionLabel}
            </Button>
            {access.landingAvailable ? (
              <Button
                variant="outlined"
                startIcon={<PublicRoundedIcon />}
                onClick={() => navigate(landingPath)}
                sx={{ color: "#fff", borderColor: "rgba(255,255,255,.35)", borderRadius: 999, px: 3, textTransform: "none", fontWeight: 900 }}
              >
                Ir a la landing
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, bgcolor: "var(--wp-panel-surface, #fff)" }}>
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          {trialEnded
            ? <>La prueba terminó, pero tu página no se elimina. Desde <strong>Tu plan</strong> podés elegir los servicios que querés mantener activos.</>
            : billingRestricted
              ? <>El pago de WebProfit es independiente de las ventas y cobros que realiza tu comercio. Regularizá la suscripción desde <strong>Tu plan</strong>.</>
              : <>La suspensión administrativa no elimina información. Revisá el estado general del proyecto para volver a habilitarlo.</>}
        </Alert>
      </CardContent>
    </Card>
  );
}
